import React, { createContext, useContext, useState, useMemo, useEffect, useRef, useCallback } from "react";
import { TALENTION_HELPERS, crearEspacioVacio, serializarEspacio, migrarEspacioConAviso, ESQUEMA_ACTUAL } from "./data.js";
import { puntosMinisterio, estadoMinisterio } from "./ministerio.js";
import { reducer } from "./reducers.js";
import { detectHierarchyIssues } from "./hierarchy.js";
import { apiGet, apiPut } from "../igualdad-api.js";
import { useIgualdad } from "../igualdad-store.jsx";

/* ============================================================
   Talention — Store del SVPT embebido

   Un espacio de trabajo por empresa, guardado como blob JSON en el backend
   (GET/PUT /clientes/{id}/svpt-espacio). Este provider es el ÚNICO punto
   de contacto con el servidor: las pantallas siguen hablando
   `useStore()` + `dispatch(acción)`, como en el prototipo.

   - Perezoso: no pide nada hasta que una pantalla llama a `activar()`.
   - Autoguardado con debounce; los PUT se serializan (nunca dos en vuelo).
   - Bloqueo optimista por `version`: un 409 recarga lo del servidor.
   - Un fallo de red no reintenta solo: se reintenta con el siguiente
     cambio o desde el chip de guardado.
   ============================================================ */

const StoreCtx = createContext(null);

const rutaEspacio = (clienteId) => `/clientes/${clienteId}/svpt-espacio`;

const MENSAJE_CONFLICTO = "Otra persona ha guardado cambios en esta empresa; se ha cargado su versión";

/* Aviso de las respuestas Ministerio que la poda quitó al cargar (§7 del spec). */
const avisoDeDescartadas = (n) => `Se ha${n === 1 ? "" : "n"} descartado ${n} respuesta${n === 1 ? "" : "s"} del método Ministerio que ya no encaja${n === 1 ? "" : "n"} en el catálogo`;

/* Puntos, estado y aviso de revisión de cada puesto, a partir de las
   respuestas del método de la empresa. Nunca se guardan: se recalculan aquí. Un puesto
   sin respuestas sale PENDIENTE con 0 puntos porque, tras pasar por el
   servidor, esos campos ya no vienen en el blob. */
function calcularPosiciones(space) {
  const scored = space.positions.map(p => {
    if (space.metodo === "ministerio") {
      const respuestas = space.ministerio.respuestas[p.id];
      const { otros } = space.ministerio;
      return { ...p, points: puntosMinisterio(respuestas, otros), status: estadoMinisterio(respuestas, otros) };
    }
    const v = space.valuations[p.id];
    if (!v) return { ...p, points: 0, status: "PENDIENTE" };
    const points = TALENTION_HELPERS.computePositionPoints(v, space.factors);
    const { total: required, done: filled } = TALENTION_HELPERS.countCompletedItems(v, space.factors);
    let status = "PENDIENTE";
    if (filled === required && required > 0) status = "FINALIZADA";
    else if (filled > 0) status = "EN PROGRESO";
    return { ...p, points, status };
  });
  // La coherencia entre jerarquía y puntuación se deduce del estado actual,
  // así que se corrige sola al revalorar o al reordenar el organigrama. Se
  // fija `review` explícitamente (a `undefined` si no hay incidencia): sin
  // esto, un `review` heredado del puesto crudo sobreviviría al recálculo.
  const issues = detectHierarchyIssues(scored);
  return scored.map(p => ({ ...p, review: issues[p.id] }));
}

/* Lee el espacio de una empresa. Sin espacio guardado, uno vacío con versión 0. */
async function leerEspacio(clienteId) {
  const res = await apiGet(rutaEspacio(clienteId));
  const { datos, esquema, version } = res.data;
  if (datos == null) return { space: crearEspacioVacio(), respuestasDescartadas: 0, version: version || 0 };
  const { space, respuestasDescartadas } = migrarEspacioConAviso(datos, esquema);
  return { space, respuestasDescartadas, version: version || 0 };
}

function cuerpoDeGuardado(space, version) {
  return { datos: serializarEspacio(space), esquema: ESQUEMA_ACTUAL, version };
}

/* Lo que necesitan los callbacks sin depender del ciclo de render: a qué
   empresa pertenece lo que hay en memoria, el espacio y su versión, y el
   estado del guardado (pendiente, en vuelo, temporizador). */
const refInicial = () => ({ clienteId: null, espacio: null, version: 0, pendiente: false, enVuelo: false, cargando: false, temporizador: null });

function SvptProvider({ children, debounceMs = 800 }) {
  const { session, fireToast } = useIgualdad();
  const clienteId = session.clienteId;

  const [espacio, setEspacio] = useState(null);
  const [version, setVersion] = useState(0);
  const [carga, setCarga] = useState("inactivo");
  const [cargaError, setCargaError] = useState(null);
  const [guardado, setGuardado] = useState("guardado");
  const [guardadoError, setGuardadoError] = useState(null);

  const ref = useRef(refInicial());
  // Las funciones de guardado se llaman desde temporizadores: se leen de un
  // ref para que siempre sea la versión del último render.
  const fns = useRef({});

  function fijarEspacio(space, v) {
    ref.current.espacio = space;
    ref.current.version = v;
    setEspacio(space);
    setVersion(v);
  }

  async function cargar(cid) {
    ref.current.clienteId = cid;
    // Guarda de carrera en un ref, no en el estado: bajo StrictMode dos
    // invocaciones seguidas de `activar()` leen el mismo `carga === "inactivo"`
    // porque el re-render con "cargando" aún no se ha aplicado. El ref sí
    // cambia al instante.
    ref.current.cargando = true;
    setCarga("cargando");
    setCargaError(null);
    try {
      const { space, respuestasDescartadas, version: v } = await leerEspacio(cid);
      if (ref.current.clienteId !== cid) return;   // se cambió de empresa mientras cargaba
      ref.current.cargando = false;
      ref.current.pendiente = false;
      fijarEspacio(space, v);
      setGuardado("guardado");
      setGuardadoError(null);
      setCarga("listo");
      if (respuestasDescartadas > 0) fireToast(avisoDeDescartadas(respuestasDescartadas));
    } catch (e) {
      if (ref.current.clienteId !== cid) return;
      ref.current.cargando = false;
      setCargaError(e?.message || "No se pudo cargar el espacio de valoración.");
      setCarga("error");
    }
  }

  function programar(cid) {
    const r = ref.current;
    clearTimeout(r.temporizador);
    r.temporizador = setTimeout(() => fns.current.guardar(cid), debounceMs);
  }

  async function recargarTrasConflicto(cid) {
    ref.current.pendiente = false;
    try {
      const { space, respuestasDescartadas, version: v } = await leerEspacio(cid);
      if (ref.current.clienteId !== cid) return;
      fijarEspacio(space, v);
      setGuardado("recargado");
      setGuardadoError(null);
      fireToast(respuestasDescartadas > 0 ? `${MENSAJE_CONFLICTO}. ${avisoDeDescartadas(respuestasDescartadas)}` : MENSAJE_CONFLICTO);
    } catch (e) {
      if (ref.current.clienteId !== cid) return;
      // La recarga en sí falló (red caída, por ejemplo): lo local no se ha
      // tocado, así que sigue habiendo un cambio pendiente. Sin esto,
      // `reintentar()` se queda mudo porque exige `pendiente`, y el chip
      // ofrece un botón que no hace nada.
      ref.current.pendiente = true;
      setGuardado("error");
      setGuardadoError(e?.message || "No se pudo recargar el espacio de valoración.");
    }
  }

  async function guardar(cid) {
    const r = ref.current;
    if (r.enVuelo || !r.pendiente || r.clienteId !== cid || !r.espacio) return;
    r.enVuelo = true;
    r.pendiente = false;
    setGuardado("guardando");
    let res;
    try {
      res = await apiPut(rutaEspacio(cid), cuerpoDeGuardado(r.espacio, r.version));
    } catch (e) {
      r.enVuelo = false;
      if (ref.current.clienteId !== cid) return;
      if (e?.status === 409) {
        await recargarTrasConflicto(cid);
        return;
      }
      r.pendiente = true;
      const mensaje = e?.message || "No se pudieron guardar los cambios del SVPT.";
      setGuardado("error");
      setGuardadoError(mensaje);
      fireToast(mensaje);
      return;
    }
    r.enVuelo = false;
    if (ref.current.clienteId !== cid) return;
    r.version = res.data.version;
    setVersion(res.data.version);
    setGuardadoError(null);
    if (r.pendiente) {
      // Hubo más cambios mientras el PUT viajaba: van en el siguiente.
      setGuardado("pendiente");
      programar(cid);
    } else {
      setGuardado("guardado");
    }
  }

  // React no permite escribir en un ref durante el render: se fija tras cada
  // render, sin lista de dependencias, así siempre es la versión del último.
  useEffect(() => {
    fns.current = { guardar, programar };
  });

  /* Aplica una acción al espacio en memoria y programa el guardado. Una
     acción que no cambia nada (el reducer devuelve el mismo objeto) no
     ensucia ni guarda. */
  const dispatch = useCallback((action) => {
    const r = ref.current;
    if (!r.espacio) return;
    const nuevo = reducer(r.espacio, action);
    if (nuevo === r.espacio) return;
    r.espacio = nuevo;
    setEspacio(nuevo);
    r.pendiente = true;
    setGuardado("pendiente");
    fns.current.programar(r.clienteId);
  }, []);

  const activar = useCallback(() => {
    if (clienteId && carga === "inactivo" && !ref.current.cargando) cargar(clienteId);
  }, [clienteId, carga]);

  // Solo tiene sentido reintentar una carga que ha fallado: si ya está lista
  // o en curso, repetirla pisaría el espacio en memoria y descartaría
  // cambios locales sin avisar.
  const reintentarCarga = useCallback(() => {
    if (clienteId && carga === "error") cargar(clienteId);
  }, [clienteId, carga]);

  const reintentar = useCallback(() => {
    const r = ref.current;
    if (r.pendiente && !r.enVuelo) fns.current.guardar(r.clienteId);
  }, []);

  /* Cambio de empresa: lo pendiente de la anterior se envía ya, con su
     versión, y el estado se suelta. Si justo había un PUT en vuelo, el
     envío puede recibir un 409 (versión desfasada): se avisa y se acepta. */
  useEffect(() => {
    const r = ref.current;
    if (r.clienteId === clienteId) return;
    clearTimeout(r.temporizador);
    if (r.pendiente && r.clienteId != null && r.espacio) {
      const anterior = r.clienteId;
      apiPut(rutaEspacio(anterior), cuerpoDeGuardado(r.espacio, r.version))
        .catch((e) => fireToast("No se pudieron guardar los últimos cambios del SVPT de la empresa anterior: " + (e?.message || "error de red")));
    }
    ref.current = refInicial();
    setEspacio(null);
    setVersion(0);
    setCarga("inactivo");
    setCargaError(null);
    setGuardado("guardado");
    setGuardadoError(null);
  }, [clienteId]);

  /* Recargar o cerrar la pestaña con cambios sin guardar: que el navegador avise. */
  useEffect(() => {
    const avisar = (e) => {
      const r = ref.current;
      if (r.pendiente || r.enVuelo) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", avisar);
    return () => {
      window.removeEventListener("beforeunload", avisar);
      clearTimeout(ref.current.temporizador);
    };
  }, []);

  const positions = useMemo(() => (espacio ? calcularPosiciones(espacio) : []), [espacio]);
  const state = useMemo(() => (espacio ? { ...espacio, positions } : null), [espacio, positions]);

  const value = useMemo(() => ({
    state, dispatch, helpers: TALENTION_HELPERS, version,
    carga, cargaError, activar, reintentarCarga,
    guardado, guardadoError, reintentar,
  }), [state, dispatch, version, carga, cargaError, activar, reintentarCarga, guardado, guardadoError, reintentar]);

  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
}

function useStore() {
  return useContext(StoreCtx);
}

export { SvptProvider, useStore, calcularPosiciones };
